import React, { useState } from "react";
import "../../../assets/fa-icons/css/fontawesome.css";
import "../../../assets/fa-icons/css/sharp-solid.css";
import { FontStyles } from "../../utils/font-styles.ts";
import { NumericInput } from "../../components/inputs/numeric-input.tsx";
import { DropdownItem, Select } from "../../components/inputs/select-dropdown.tsx";
import { InputIcon } from "../../components/input-icon.tsx";
import { useReportCalculatorInteraction } from "../hooks/use-report-calculator-interaction.ts";

interface MortgageCalculatorProps {
  price: number;
}

const options: DropdownItem[] = Array.from({ length: 36 }, (_, i) => ({
  value: (i + 5).toString(),
  name: `${(i + 5).toString()} years`,
}));

export const MortgageCalculator: React.FC<MortgageCalculatorProps> = ({ price }) => {
  const [propertyPrice, setPropertyPrice] = useState<number>(price);
  const [deposit, setDeposit] = useState(Math.ceil(price * 0.1));
  const [termOnLoan, setTermOnLoan] = useState(30);
  const [interestRate, setInterestRate] = useState(5);

  const reportFirstInteraction = useReportCalculatorInteraction("repayments");

  const term = termOnLoan * 12;
  const interest = interestRate / 1200.0;
  const loan = propertyPrice - deposit;
  const instalment =
    interest > 0 ? Math.max(0, Math.round((interest / (Math.pow(1 + interest, term) - 1) + interest) * loan)) : 0;
  const interestOnly = interest > 0 ? Math.max(0, Math.round(interest * loan)) : 0;

  return (
    <div className={"flex flex-col gap-4 pt-4 md:gap-6 md:pt-6"}>
      <div
        className={`${FontStyles["body-md"]} bg-cloud text-slate p-3.5 md:py-4 md:px-5.5 border-neutral-200 border-2 rounded-md`}
      >
        Use our mortgage calculator to discover your monthly repayments. These estimates are provided as illustrative
        figures for guidance only. Consider seeking advice from a professional mortgage advisor.
      </div>
      <div className={"flex flex-col gap-3.5 md:gap-6"}>
        <div className={"flex flex-col md:flex-row gap-3.5 md:gap-6.5"}>
          <NumericInput
            state={"default"}
            value={propertyPrice}
            renderLeadingIcon={() => <InputIcon icon={`fa-solid fa-sharp fa-sterling-sign`} />}
            label={"Property price"}
            labelSize="large"
            placeholder={"Enter property price"}
            onValueChange={(value) => {
              reportFirstInteraction();
              setPropertyPrice(value);
            }}
          />
          <NumericInput
            state={"default"}
            value={deposit}
            renderLeadingIcon={() => <InputIcon icon={`fa-solid fa-sharp fa-sterling-sign`} />}
            label={"Deposit"}
            labelSize="large"
            placeholder={"Enter deposit"}
            onValueChange={(value) => {
              reportFirstInteraction();
              setDeposit(Math.ceil(value));
            }}
          />
        </div>
        <div className={"flex flex-col md:flex-row gap-3.5 md:gap-6.5"}>
          <Select
            label={"Term on loan"}
            labelSize="large"
            options={options}
            onValueChange={(item) => {
              reportFirstInteraction();
              setTermOnLoan(Number(item));
            }}
            value={termOnLoan.toString()}
          />
          <NumericInput
            state={"default"}
            value={interestRate}
            label={"Interest rate"}
            labelSize="large"
            placeholder={"Enter interest rate"}
            renderTrailingIcon={() => <InputIcon icon={`fa-solid fa-sharp fa-percent`} />}
            onValueChange={(value) => {
              reportFirstInteraction();
              setInterestRate(value);
            }}
          />
        </div>
      </div>
      <div
        className={`${FontStyles["body-l"]} leading-snug md:leading-snug text-neutral-black p-3 bg-loch-50 
        border border-loch rounded-md text-center`}
      >
        Your monthly repayments would be{" "}
        <span className={"font-semibold text-small-h5 md:text-h3"}>£{instalment.toLocaleString("en-GB")}</span>
        <br />
        (or <span className={"font-semibold text-small-h5 md:text-h3"}>£{interestOnly.toLocaleString("en-GB")}</span> if
        you are repaying interest only)
      </div>
    </div>
  );
};
